import { useCallback, useRef, useState } from 'react';

/** Right accepts, left ignores, up passes over without deciding. */
export type SwipeDirection = 'right' | 'left' | 'up';

/** How far the card travels before letting go of it decides anything. */
const THRESHOLD_PX = 110;

/**
 * How far the pointer has to move before a release is a drag rather than a
 * click. A drag that starts on a link travels well past this on its way to
 * `THRESHOLD_PX`; a tap that is just a click never does.
 */
const CLICK_TRAVEL_PX = 6;

export interface SwipeState {
  dragging: boolean;
  /** Which way letting go now would decide, or null while it is still a nudge. */
  past: SwipeDirection | null;
}

const STILL: SwipeState = { dragging: false, past: null };

export interface SwipeHandlers {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  /**
   * Capture-phase, so it runs before the link or button underneath decides
   * whether to act: a release that dragged is not the click it looks like.
   */
  onClickCapture: (event: React.MouseEvent<HTMLElement>) => void;
}

function pastOf(dx: number, dy: number): SwipeDirection | null {
  if (dy < -THRESHOLD_PX && Math.abs(dy) > Math.abs(dx)) return 'up';
  if (dx > THRESHOLD_PX) return 'right';
  if (dx < -THRESHOLD_PX) return 'left';
  return null;
}

export interface SwipeOptions {
  onSwipe: (direction: SwipeDirection) => void;
  /**
   * Where the card is, every pointer move. This is a callback rather than a
   * piece of state on purpose: the card follows the hand by writing custom
   * properties on its own element, so a drag costs no React render at all and
   * only crossing the threshold does.
   */
  onMove: (offset: { dx: number; dy: number }) => void;
  enabled: boolean;
}

/**
 * A card a hand can throw. Once a press has moved, the pointer is captured on
 * the card itself, so a drag that leaves it — which is most of them, since the
 * card is being thrown off the screen — still reports where it went.
 */
export function useSwipe(options: SwipeOptions): { state: SwipeState; handlers: SwipeHandlers } {
  const [state, setState] = useState<SwipeState>(STILL);
  const from = useRef<{ x: number; y: number; id: number } | null>(null);
  // Whether the current (or just-finished) gesture moved past a click. Read
  // once by the click that follows a pointerup, then cleared — the next
  // gesture's own pointerdown/end pair overwrites it before its own click
  // could ever see a stale value.
  const traveled = useRef(false);
  const suppressClick = useRef(false);
  const { onSwipe, onMove, enabled } = options;

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) return;
      // A button on the card is still a button; a link is dragged like the
      // rest of the card and only kept from navigating if the drag travelled.
      if (event.target instanceof Element && event.target.closest('button') !== null) return;
      from.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
      traveled.current = false;
      setState({ dragging: true, past: null });
    },
    [enabled],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const start = from.current;
      if (start === null || start.id !== event.pointerId) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (!traveled.current && (Math.abs(dx) > CLICK_TRAVEL_PX || Math.abs(dy) > CLICK_TRAVEL_PX)) {
        traveled.current = true;
        // Captured only once this is a real drag, not from pointerdown: a
        // browser retargets the click that follows a capture to the capturing
        // element, so capturing on every press would swallow a plain click on
        // a link along with the drag it is meant to protect against.
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      onMove({ dx, dy });
      const past = pastOf(dx, dy);
      setState((current) => (current.past === past ? current : { dragging: true, past }));
    },
    [onMove],
  );

  const end = useCallback(
    (event: React.PointerEvent<HTMLElement>, decide: boolean) => {
      const start = from.current;
      if (start === null || start.id !== event.pointerId) return;
      from.current = null;
      suppressClick.current = traveled.current;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      const past = pastOf(event.clientX - start.x, event.clientY - start.y);
      onMove({ dx: 0, dy: 0 });
      setState(STILL);
      if (decide && past !== null) onSwipe(past);
    },
    [onSwipe, onMove],
  );

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLElement>) => end(event, true),
    [end],
  );
  const onPointerCancel = useCallback(
    (event: React.PointerEvent<HTMLElement>) => end(event, false),
    [end],
  );

  const onClickCapture = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    event.preventDefault();
  }, []);

  return {
    state,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture },
  };
}
